// Brown University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Brown's Common Data Set 2025-2026, published by the Office of
//    Institutional Research as an interactive Tableau view
//    (tableau.brown.edu, embedded in the OIR Common Data Set page; Select
//    Year 2025-2026). There is no PDF, so the OIR page is cited. Read: C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy and score date, C21/C22 plans with the early
//    decision counts for the Fall 2025 entering class. C8 in the view names
//    no entry year. G1 gives 2026-27 charges; costs come from the Office of
//    Financial Aid's 2026-27 Cost of Attendance, which itemizes them (same
//    tuition, housing and food; G5 shows books and transportation as $0).
//  - admission.brown.edu: Application Checklist (dates), Early Decision,
//    Standardized Testing. These pages carry no year label; they are the
//    pages as posted for the cycle open now (Fall 2027 entry). The
//    Regular Decision page says decisions come "by early April"; the
//    checklist and CDS C16 say late March, which is shown.
//  - College Scorecard API, school id 217156 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Brown Common Data Set 2025-2026 (Office of Institutional Research)',
        url: 'https://oir.brown.edu/institutional-data/common-data-set',
        read: '2026-10-01',
    },
    checklist: {
        label: 'Brown Admission, First-Year Application Checklist',
        url: 'https://admission.brown.edu/first-year/application-checklist',
        read: '2026-10-01',
    },
    ed: {
        label: 'Brown Admission, Early Decision',
        url: 'https://admission.brown.edu/first-year/early-decision',
        read: '2026-10-01',
    },
    tests: {
        label: 'Brown Admission, Standardized Testing',
        url: 'https://admission.brown.edu/first-year/standardized-tests',
        read: '2026-10-01',
    },
    coa: {
        label: 'Brown Office of Financial Aid, Cost of Attendance 2026-27',
        url: 'https://finaid.brown.edu/estimate-cost-aid/cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?217156-Brown-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'brown-university',
    kbId: 'brown_university',
    name: 'Brown University',
    shortName: 'Brown',
    location: 'Providence, RI',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Brown visual identity policy); decoration only.
    branding: {
        primary: '#4E3629',
        secondary: '#ED1C24',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/brown_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 42774,
        admitted: 2710,
        enrolled: 1719,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 559, admitted: 63 },
            { group: 'Out-of-state', applied: 32351, admitted: 2243 },
            { group: 'International', applied: 9861, admitted: 404 },
            { group: 'Unknown', applied: 3, admitted: 0 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Brown\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by concentration.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Brown has no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 5062, admitted: 907 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Late March', binding: false },
        ],
        note: 'Deadlines are 11:59 p.m. in your local time. You apply through the Common Application, with the $80 fee or a fee waiver. Early Decision applicants, a parent or guardian and the school counselor sign an Early Decision Agreement, and the counselor sends first-quarter or first-trimester grades. The midyear report is due February 27. Optional music or visual art work goes through SlideRoom by November 4 for Early Decision and QuestBridge Match applicants and by January 7 for Regular Decision. The eight-year Program in Liberal Medical Education (PLME) and the five-year Brown and RISD Dual Degree Program ask for their own essays. Regular Decision applicants reply by May 1.',
        source: 'checklist',
        binding: 'Early Decision is binding: if admitted, you withdraw all other applications, including regular, rolling and early action ones. While you apply Early Decision to Brown you may not apply to another school\'s early decision, single-choice early action or restrictive early action plan. A deferred application is read again with Regular Decision; a denied applicant may not reapply that year. Brown says it makes the same decisions under Early Decision that it would under Regular Decision.',
        bindingSource: 'ed',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Brown requires the SAT or ACT of first-year applicants, as it has since the 2024-25 cycle. There is no minimum score, and scores are read in the context you are applying from. Brown superscores, and you can report your scores yourself on the Common App; official scores are needed only if you are admitted and enroll. The SAT essay and the ACT writing and science sections are optional. Early Decision applicants should test by the last October test date, Regular Decision applicants by the last December test date.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission, with scores due by January 5. Its one exception is first-year veterans who apply through the veteran application.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 74568 },
            { item: 'Fees (with the $100 academic record fee for first-year students)', amount: 3084 },
            { item: 'Housing', amount: 10710 },
            { item: 'Food', amount: 8754 },
            { item: 'Personal expenses', amount: 2878 },
            { item: 'Books and course materials', amount: null },
            { item: 'Travel', amount: null },
        ],
        total: 99994,
        note: 'For an incoming first-year student, fall and spring semesters. Books and course materials are covered by Brown for most students on University Scholarship; other students\' budgets carry a $1,300 allowance. Travel is set by home address. Neither is in the $99,994 total. Health insurance is required and is not included.',
        source: 'coa',
        netPrice: {
            average: 26976,
            byIncome: [
                { income: '$0 to $30,000', amount: 39 },
                { income: '$30,001 to $48,000', amount: 228 },
                { income: '$48,001 to $75,000', amount: 10079 },
                { income: '$75,001 to $110,000', amount: 24827 },
                { income: 'Over $110,000', amount: 48459 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Brown\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
